/**
 * PuzzleAvatar — Caissa Rive character with state-driven animations.
 *
 * idle/loading/incorrect → caissa_animation.riv  (rive.play switches clips)
 * correct                → CaissaSuccess_animation.riv  (dedicated celebration)
 *
 * Using the success file directly for correct guarantees the full celebration
 * animation plays rather than relying on rive.play() finding a named clip.
 * The component is keyed on the src so it remounts only when the source file
 * changes (idle ↔ loading ↔ incorrect stay in the same Rive instance).
 */
import React, {useEffect} from 'react';
import {StyleSheet, View} from 'react-native';
import {Alignment, Fit, Layout, useRive} from '@rive-app/react-canvas';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
// @ts-ignore
import caissahello_animation from '@/Assets/Animation/CaissaHello_animation.riv';
// @ts-ignore
import caissasuccess_animation from '@/Assets/Animation/CaissaSuccess_animation.riv';

export type AvatarStatus = 'idle' | 'loading' | 'correct' | 'incorrect';

// caissa_animation.riv renders as a tiny circular head (chatbot button style)
// → only use the two properly-framed dedicated files.
const SRC: Record<AvatarStatus, string> = {
  idle:      caissahello_animation,    // Caissa at rest / waving gently
  loading:   caissahello_animation,    // same file, same frame = same big size
  correct:   caissasuccess_animation,  // Caissa celebrating
  incorrect: caissahello_animation,    // revert to hello then label signals error
};

// No clip overrides needed — all states autoplay their file's default animation
const CLIP: Partial<Record<AvatarStatus, string>> = {};

const LABEL: Record<AvatarStatus, string | null> = {
  idle:      null,
  loading:   'Thinking…',
  correct:   'Brilliant!',
  incorrect: 'Try again!',
};

const LABEL_COLOR: Record<AvatarStatus, string> = {
  idle:      CCColors.TextColor.Grey,
  loading:   CCColors.Primary.Brown,
  correct:   CCColors.Green,
  incorrect: CCColors.Red,
};

// ── Inner Rive component — remounts when src changes ──────────────────────
function RiveAvatar({status, size}: {status: AvatarStatus; size: number}) {
  const {RiveComponent, rive} = useRive({
    src: SRC[status],
    autoplay: true,
    layout: new Layout({fit: Fit.Contain, alignment: Alignment.Center}),
  });

  // Switch named clip for states sharing the same .riv file
  useEffect(() => {
    if (!rive) return;
    const clip = CLIP[status];
    if (clip) rive.play(clip);
  }, [status, rive]);

  return (
    <View style={{width: size, height: size}}>
      <RiveComponent />
    </View>
  );
}

// ── Public component ──────────────────────────────────────────────────────
interface PuzzleAvatarProps {
  status: AvatarStatus;
  size?: number;
  labelPosition?: 'below' | 'right' | 'none';
}

export default function PuzzleAvatar({
  status,
  size = 110,
  labelPosition = 'right',
}: PuzzleAvatarProps) {
  const label = LABEL[status];
  const isRow = labelPosition === 'right';

  return (
    <View style={[styles.container, isRow && styles.containerRow]}>
      {/* key on src so Rive remounts only when switching correct ↔ others */}
      <RiveAvatar key={SRC[status]} status={status} size={size} />
      {labelPosition !== 'none' && label ? (
        <CCText style={[
          styles.label,
          {color: LABEL_COLOR[status]},
          isRow && styles.labelRight,
        ]}>
          {label}
        </CCText>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container:    {alignItems: 'center', justifyContent: 'center'},
  containerRow: {flexDirection: 'row', alignItems: 'center'},
  label:        {fontSize: 13, fontWeight: '700', marginTop: 4, textAlign: 'center'},
  labelRight:   {marginTop: 0, marginLeft: 8},
});
